import { FC, useState, useMemo } from 'react'
import { Flex, Button, Modal, Tag, Tooltip } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { formatCurrency, formatCurrencyLargeCompact, formatInt } from 'uikit/utils/number-formatters'
import type { NumberFormatOptions } from 'uikit/utils/number-formatters'
import { useCurrency } from 'contexts/CurrencyContext'
import { StatValue, PlatformIconLink } from '../components'
import ReviewDetailModal from '../Reviews/ReviewDetailModal'
import ListingPopup from './ListingPopup'
import { ITableColumnsType, Table, TableProvider } from 'uikit/tables/default'
import { ESTIMATED_REVENUE_MIN_MULTIPLIER, ESTIMATED_REVENUE_MAX_MULTIPLIER, NOT_AVAILABLE } from '../utils'

export interface ListingMarker {
  id: string
  platform: string
  price?: number
  lat: number
  lng: number
  title: string
  address?: string
  estimatedRevenue: number
  numberOfReviews?: number
  images: string[]
}

export interface ListingStats {
  totalListings: number
  activeListings: number
  estimatedRevenue: number
  reportedRevenue: number | null
  reportedExpenses: number | null
  expensesRatio: number | null
}

export interface ListingRow {
  listingId: string
  platform: string
  url: string
  isActive?: boolean
  address?: string
  jurisdiction?: string
  estimatedRevenue: number
  propertyType?: string
  reviewsCount?: number
  ownerName?: string
  licenseNum?: string
}

const ListingSummary: FC<{ stats: ListingStats; currencyOpts: NumberFormatOptions }> = ({ stats, currencyOpts }) => (
  <Flex
    style={{
      border: '1px solid #f0f0f0',
      borderRadius: 8,
      padding: '16px 0',
    }}
  >
    <StatValue label='Total Listings' value={stats.totalListings.toLocaleString()} />
    <StatValue
      label={
        <span>
          <span
            style={{
              display: 'inline-block',
              width: 10,
              height: 10,
              borderRadius: '50%',
              backgroundColor: '#52c41a',
              marginRight: 6,
            }}
          />
          Active Listings
        </span>
      }
      value={stats.activeListings.toLocaleString()}
    />
    <StatValue
      label={
        <Flex align='center' justify='center' gap={6}>
          <span>Estimated Revenue</span>
          <Tooltip
            title='The estimated revenue range is based on IVIX’s calculations and is not self reported by the host or by Airbnb.'
            placement='bottom'
            color='#fff'
          >
            <InfoCircleOutlined style={{ color: '#B8AFAF', fontSize: 14 }} />
          </Tooltip>
        </Flex>
      }
      value={`${formatCurrencyLargeCompact(
        stats.estimatedRevenue * ESTIMATED_REVENUE_MIN_MULTIPLIER,
        currencyOpts,
      )} - ${formatCurrencyLargeCompact(stats.estimatedRevenue * ESTIMATED_REVENUE_MAX_MULTIPLIER, currencyOpts)}`}
    />
    <StatValue label='Reported Revenue' value={formatCurrency(stats.reportedRevenue, currencyOpts)} />
  </Flex>
)

const createMarkerIcon = (label: string): L.DivIcon =>
  new L.DivIcon({
    className: 'str-price-marker',
    html: `<div style="
      background: white;
      border: 1px solid #d9d9d9;
      border-radius: 20px;
      padding: 4px 10px;
      font-size: 13px;
      font-weight: 600;
      font-family: Source Sans Pro, sans-serif;
      white-space: nowrap;
      box-shadow: 0 2px 6px rgba(0,0,0,0.15);
      color: #1C2842;
      display: inline-block;
      transform: translate(-50%, -50%);
      max-width: 140px;
      overflow: hidden;
      text-overflow: ellipsis;
    ">${label}</div>`,
    iconSize: [0, 0],
    iconAnchor: [0, 0],
  })

const getMarkerLabel = (marker: ListingMarker, currencyOpts: NumberFormatOptions): string => {
  if (marker.price != null) return formatCurrency(marker.price, currencyOpts) ?? marker.title
  return marker.title
}

const DEFAULT_CENTER: L.LatLngExpression = [38.9096, -77.0434]

const ListingMap: FC<{ markers: ListingMarker[]; currencyOpts: NumberFormatOptions }> = ({ markers, currencyOpts }) => {
  const mapCenter: L.LatLngExpression = useMemo(() => {
    if (markers.length === 0) return DEFAULT_CENTER
    const avgLat = markers.reduce((sum, m) => sum + m.lat, 0) / markers.length
    const avgLng = markers.reduce((sum, m) => sum + m.lng, 0) / markers.length
    return [avgLat, avgLng]
  }, [markers])

  const markerIcons = useMemo(
    () =>
      markers.reduce<Record<string, L.DivIcon>>((acc, m) => {
        acc[m.id] = createMarkerIcon(getMarkerLabel(m, currencyOpts))
        return acc
      }, {}),
    [markers, currencyOpts],
  )

  return (
    <div style={{ height: 400, borderRadius: 8, overflow: 'hidden' }}>
      <MapContainer
        center={mapCenter}
        zoom={14}
        style={{ width: '100%', height: '100%' }}
        zoomControl={true}
        attributionControl={true}
        scrollWheelZoom={false}
      >
        <TileLayer
          url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        />
        {markers.map(marker => (
          <Marker key={marker.id} position={[marker.lat, marker.lng]} icon={markerIcons[marker.id]}>
            <Popup maxWidth={320} closeButton={false}>
              <ListingPopup listing={marker} />
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  )
}

const getListingColumns = (
  onMoreDetails: (record: ListingRow) => void,
  currencyOpts: NumberFormatOptions,
): ITableColumnsType<ListingRow> => [
  {
    title: 'ID',
    dataIndex: 'listingId',
    key: 'listingId',
    width: 180,
    fixed: 'left',
    minWidth: 180,
  },
  {
    title: 'URL',
    key: 'url',
    width: 80,
    minWidth: 80,
    render: (_: unknown, record: ListingRow) => (
      <PlatformIconLink platform={record.platform} url={record.url} size={16} />
    ),
  },
  {
    title: 'Status',
    dataIndex: 'isActive',
    key: 'isActive',
    width: 90,
    minWidth: 90,
    render: (val?: boolean) => (
      <Tag color={val ? 'success' : 'error'} style={{ margin: 0 }}>
        {val ? 'Active' : 'Inactive'}
      </Tag>
    ),
  },
  {
    title: 'Listing Address',
    dataIndex: 'address',
    key: 'address',
    width: 200,
    minWidth: 200,
    ellipsis: true,
    render: (val?: string) => val ?? 'N/A',
  },
  {
    title: 'Jurisdiction',
    dataIndex: 'jurisdiction',
    key: 'jurisdiction',
    width: 180,
    minWidth: 180,
    render: (val?: string) => val ?? 'N/A',
  },
  {
    title: 'Est. Revenue',
    dataIndex: 'estimatedRevenue',
    key: 'estimatedRevenue',
    width: 220,
    minWidth: 220,
    render: (val?: number | null) => {
      if (val == null || val === 0) return NOT_AVAILABLE
      return `${formatCurrency(val * ESTIMATED_REVENUE_MIN_MULTIPLIER, currencyOpts)} - ${formatCurrency(
        val * ESTIMATED_REVENUE_MAX_MULTIPLIER,
        currencyOpts,
      )}`
    },
  },
  {
    title: 'Property Type',
    dataIndex: 'propertyType',
    key: 'propertyType',
    width: 150,
    minWidth: 150,
    render: (val?: string) => val ?? 'N/A',
  },
  {
    title: 'Reviews',
    dataIndex: 'reviewsCount',
    key: 'reviewsCount',
    width: 100,
    minWidth: 100,
    render: (val?: number) => formatInt(val),
  },
  {
    title: 'License owner',
    dataIndex: 'ownerName',
    key: 'ownerName',
    width: 150,
    minWidth: 150,
    render: (val?: string) => val ?? 'N/A',
  },
  {
    title: 'License Number',
    dataIndex: 'licenseNum',
    key: 'licenseNum',
    width: 150,
    minWidth: 150,
    render: (val?: string) => val ?? 'N/A',
  },
  {
    title: '',
    key: 'action',
    width: 120,
    minWidth: 120,
    fixed: 'right',
    render: (_: unknown, record: ListingRow) => (
      <Button
        type='default'
        style={{ padding: '8px 16px', height: 'auto', lineHeight: 1 }}
        onClick={() => onMoreDetails(record)}
      >
        More Details
      </Button>
    ),
  },
]

const DEFAULT_PAGE_SIZE = 10
const PAGE_SIZE_OPTIONS = [10, 25, 50]

const ListingReviewsTable: FC<{ rows: ListingRow[]; activityIndex: string; currencyOpts: NumberFormatOptions }> = ({
  rows,
  activityIndex,
  currencyOpts,
}) => {
  const [selectedListing, setSelectedListing] = useState<ListingRow | null>(null)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const reviewColumns = useMemo(
    () => getListingColumns(record => setSelectedListing(record), currencyOpts),
    [currencyOpts],
  )

  return (
    <div style={{ gap: 16 }} className='d-flex flex-column'>
      <TableProvider columns={reviewColumns}>
        <Table<ListingRow>
          rowKey='listingId'
          dataSource={rows}
          pagination={{
            pageSize,
            showSizeChanger: true,
            pageSizeOptions: PAGE_SIZE_OPTIONS,
            onShowSizeChange: (_current, size) => setPageSize(size),
          }}
          size='middle'
          bordered={false}
        />
      </TableProvider>

      <Modal
        open={!!selectedListing}
        onCancel={() => setSelectedListing(null)}
        footer={null}
        width={1024}
        destroyOnHidden
        styles={{ body: { padding: 0 }, content: { padding: 0 } }}
      >
        {selectedListing && <ReviewDetailModal listing={selectedListing} activityIndex={activityIndex} />}
      </Modal>
    </div>
  )
}

export interface ListingsProps {
  listingStats: ListingStats
  listingMarkers: ListingMarker[]
  listingRows: ListingRow[]
  activityIndex: string
}

const Listings: FC<ListingsProps> = ({ listingStats, listingMarkers, listingRows, activityIndex }) => {
  const { currentCurrency } = useCurrency()
  const currencyOpts = useMemo(() => ({ currency: currentCurrency }), [currentCurrency])

  return (
    <div className='d-flex flex-column' style={{ gap: 32 }}>
      <ListingSummary stats={listingStats} currencyOpts={currencyOpts} />
      <ListingMap markers={listingMarkers} currencyOpts={currencyOpts} />
      <ListingReviewsTable rows={listingRows} activityIndex={activityIndex} currencyOpts={currencyOpts} />
    </div>
  )
}

export default Listings
